import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { manualPunchApi, REASON_OPTIONS } from '../api/manualPunchApi';
import { appToast } from '../../../../components/AppToast';

const DEFAULT_FORM = {
  emp_id: '',
  emp_name: '',
  employeeId: '',
  date: new Date().toISOString().slice(0, 10),
  hour: '09',
  minute: '00',
  ampm: 'am',
  reason_id: '',
  remarks: '',
  gpsLocation: '',
  status: 'Pending',
  firstPunch: '',
  lastPunch: '',
};

export function useManualPunchEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const isEdit = Boolean(id);

  const [form, setForm] = useState(DEFAULT_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [reasonOptions, setReasonOptions] = useState(REASON_OPTIONS);

  // Load dynamic reasons
  useEffect(() => {
    manualPunchApi
      .getOptions()
      .then((opts) => {
        if (Array.isArray(opts?.reasons) && opts.reasons.length > 0) {
          const mapped = opts.reasons.map((r) => ({
            value: String(r.ID || r.id),
            label: r.Name || r.name,
            id: r.ID || r.id,
          }));
          setReasonOptions(mapped);
          if (!isEdit && !form.reason_id) {
            setForm((prev) => ({ ...prev, reason_id: String(mapped[0].value) }));
          }
        }
      })
      .catch(() => {});
  }, [isEdit, form.reason_id]);

  const loadData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const found = await manualPunchApi.getRecordById(id);
      if (found) {
        setForm({
          emp_id: found.emp_id ? String(found.emp_id) : '',
          emp_name: found.employee || found.emp_name || '',
          employeeId: found.employeeId || `EMP-${found.emp_id}`,
          date: found.date || new Date().toISOString().slice(0, 10),
          hour: found.hour || '09',
          minute: found.minute || '00',
          ampm: found.ampm || 'am',
          reason_id: found.reason_id ? String(found.reason_id) : '',
          remarks: found.remarks || '',
          gpsLocation: found.gpsLocation || '',
          status: found.status || 'Pending',
          firstPunch: found.firstPunch || '',
          lastPunch: found.lastPunch || '',
        });
      } else {
        appToast.error('Record not found.');
        navigate('/payroll/manual-punch');
      }
    } catch (err) {
      console.error(err);
      appToast.error('Failed to load manual punch details.');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEdit) {
      loadData();
    }
  }, [isEdit, loadData]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (options = { exitAfter: true, customStatus: null }) => {
    if (!form.emp_id) {
      appToast.error('Please select an employee.');
      return;
    }
    if (!form.reason_id) {
      appToast.error('Please select a reason.');
      return;
    }
    if (String(form.reason_id) === '5' && (!form.remarks || !form.remarks.trim())) {
      appToast.error('Please enter remarks for reason Other.');
      return;
    }

    setIsSaving(true);
    try {
      if (isEdit) {
        await manualPunchApi.updateRecord(id, form);
        if (options.customStatus && options.customStatus !== form.status) {
          await manualPunchApi.updateStatus(id, options.customStatus);
        }
        appToast.success(`Manual punch for "${form.emp_name || 'Employee'}" updated successfully.`);
      } else {
        const created = await manualPunchApi.createRecord(form);
        if (options.customStatus === 'Approved' && created?.id) {
          await manualPunchApi.updateStatus(created.id, 'Approved');
        }
        appToast.success(`Manual punch request submitted successfully.`);
      }

      if (options.exitAfter) {
        navigate('/payroll/manual-punch');
      } else {
        setForm({
          ...DEFAULT_FORM,
          emp_id: form.emp_id,
          emp_name: form.emp_name,
        });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save manual punch.';
      appToast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleApproveAndExit = () => handleSave({ exitAfter: true, customStatus: 'Approved' });
  const handleRejectAndExit = () => handleSave({ exitAfter: true, customStatus: 'Rejected' });

  return {
    state: {
      form,
      isEdit,
      id,
      isLoading,
      isSaving,
      reasonOptions,
    },
    actions: {
      patchForm,
      setForm,
      handleSave,
      handleApproveAndExit,
      handleRejectAndExit,
      navigate,
    },
  };
}
